import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack, TextField } from '@mui/material';
import { salonApi } from '../services/salonService';
import { PageHeader, SectionCard, FormSection, FieldGrid } from '../components/ui';
import { tokens } from '../theme';

type FieldErrors = Partial<Record<'name' | 'description' | 'phone' | 'email' | 'address' | 'city' | 'postalCode', string>>;

export const CreateSalonPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (fieldErrors[e.target.name as keyof FieldErrors]) {
      setFieldErrors({ ...fieldErrors, [e.target.name]: undefined });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);

    try {
      const salon = await salonApi.create({
        name: form.name,
        address: form.address,
        city: form.city,
        postalCode: form.postalCode,
        ...(form.description ? { description: form.description } : {}),
        ...(form.phone ? { phone: form.phone } : {}),
        ...(form.email ? { email: form.email } : {}),
      });
      navigate(`/salons/${salon.id}`);
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string; details?: { field?: string; message: string }[] } } }).response?.data
          : undefined;
      const details = data?.details || [];
      if (details.length > 0) {
        const next: FieldErrors = {};
        details.forEach((d) => {
          if (d.field && !next[d.field as keyof FieldErrors]) next[d.field as keyof FieldErrors] = d.message;
        });
        setFieldErrors(next);
        setError('Popraw zaznaczone pola i spróbuj ponownie.');
      } else {
        setError(data?.error || 'Nie udało się utworzyć salonu. Spróbuj ponownie za chwilę.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Salony', to: '/salons' }]}
        title="Nowy salon"
        description="Podaj podstawowe dane. Usługi, zespół i godziny otwarcia ustawisz zaraz po utworzeniu salonu."
      />

      <SectionCard sx={{ maxWidth: 960 }}>
        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <FormSection first title="Podstawowe informacje" description="Nazwa i opis są widoczne dla klientów przy rezerwacji.">
            <Stack gap={2}>
              <TextField
                fullWidth
                required
                label="Nazwa salonu"
                name="name"
                placeholder="np. Studio Urody Bella"
                value={form.name}
                onChange={handleChange}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Opis"
                name="description"
                placeholder="Czym się zajmujecie, co wyróżnia salon"
                value={form.description}
                onChange={handleChange}
                error={!!fieldErrors.description}
                helperText={fieldErrors.description || `Opcjonalnie, do 500 znaków (${form.description.length}/500).`}
              />
            </Stack>
          </FormSection>

          <FormSection title="Kontakt" description="Pod tym numerem i adresem klienci mogą zapytać o wizytę.">
            <FieldGrid columns={2}>
              <TextField
                fullWidth
                label="Telefon"
                name="phone"
                type="tel"
                placeholder="+48 123 456 789"
                value={form.phone}
                onChange={handleChange}
                error={!!fieldErrors.phone}
                helperText={fieldErrors.phone || 'Opcjonalnie.'}
              />
              <TextField
                fullWidth
                label="E-mail"
                name="email"
                type="email"
                placeholder="kontakt@salon.pl"
                value={form.email}
                onChange={handleChange}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email || 'Opcjonalnie.'}
              />
            </FieldGrid>
          </FormSection>

          <FormSection title="Adres" description="Adres salonu, pod który przychodzą klienci.">
            <Stack gap={2}>
              <TextField
                fullWidth
                required
                label="Ulica i numer"
                name="address"
                placeholder="ul. Warszawska 10"
                value={form.address}
                onChange={handleChange}
                error={!!fieldErrors.address}
                helperText={fieldErrors.address}
              />
              <FieldGrid columns={2}>
                <TextField
                  fullWidth
                  required
                  label="Kod pocztowy"
                  name="postalCode"
                  placeholder="00-000"
                  value={form.postalCode}
                  onChange={handleChange}
                  inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                  error={!!fieldErrors.postalCode}
                  helperText={fieldErrors.postalCode || 'W formacie 00-000.'}
                />
                <TextField
                  fullWidth
                  required
                  label="Miasto"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  error={!!fieldErrors.city}
                  helperText={fieldErrors.city}
                />
              </FieldGrid>
            </Stack>
          </FormSection>

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            justifyContent="flex-end"
            gap={1}
            sx={{ pt: 2.5, borderTop: `1px solid ${tokens.line}` }}
          >
            <Button variant="text" onClick={() => navigate('/salons')} disabled={loading}>
              Anuluj
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {loading ? 'Tworzenie salonu…' : 'Utwórz salon'}
            </Button>
          </Stack>
        </Box>
      </SectionCard>
    </Box>
  );
};
